import { useState, useMemo, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import {
  Rocket, PenLine, FileText, Layers, History as HistoryIcon, ShieldCheck,
  Users, Building2, CreditCard, HelpCircle, Check, Lock, Eye, EyeOff, ArrowRight,
  LayoutTemplate, Sparkles,
} from "lucide-react";

const ROLE_ORDER = ["employee", "supervisor", "org_admin", "system_admin"];
const atLeast = (role, min) => ROLE_ORDER.indexOf(role) >= ROLE_ORDER.indexOf(min);

const Step = ({ n, title, children }) => (
  <div className="flex gap-4">
    <div className="h-8 w-8 shrink-0 rounded-full bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] font-mono text-sm flex items-center justify-center">{n}</div>
    <div>
      <p className="font-medium text-navy">{title}</p>
      <p className="text-sm text-slatebody mt-1 leading-relaxed">{children}</p>
    </div>
  </div>
);

const Bullet = ({ children }) => (
  <li className="flex gap-2.5 text-sm text-slatebody leading-relaxed">
    <Check className="h-4 w-4 text-[#202123] shrink-0 mt-0.5" /> <span>{children}</span>
  </li>
);

const Card = ({ id, icon: Icon, title, badge, children }) => (
  <section id={id} className="scroll-mt-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid={`help-section-${id}`}>
    <div className="flex items-center gap-3 mb-4">
      <div className="h-10 w-10 rounded-xl bg-[#ECECEC]/10 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-[#202123]" /></div>
      <h2 className="font-display text-xl font-semibold text-navy">{title}</h2>
      {badge && <span className="ml-auto font-mono text-[10px] uppercase tracking-wide text-[#202123] bg-[#ECECEC]/10 px-2 py-1 rounded-full">{badge}</span>}
    </div>
    <div className="space-y-3">{children}</div>
  </section>
);

const linkCls = "text-[#202123] font-medium hover:underline";

export default function Help() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isOrg = user?.account_type === "organization";
  const isSupervisor = user?.can_view_team === true;
  const isAdmin = user?.is_primary_admin === true || user?.is_org_admin === true;
  const [active, setActive] = useState("welcome");

  const toc = useMemo(() => [
    { id: "welcome", show: true },
    { id: "quickstart", show: true },
    { id: "logging", show: true },
    { id: "reports", show: true },
    { id: "styles", show: true },
    { id: "customtpl", show: true },
    { id: "context", show: true },
    { id: "history", show: true },
    { id: "privacy", show: true },
    { id: "team", show: isSupervisor },
    { id: "org", show: isAdmin },
    { id: "billing", show: true },
    { id: "faq", show: true },
  ].filter((x) => x.show), [isSupervisor, isAdmin]);

  const go = (id) => {
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const location = useLocation();
  useEffect(() => {
    const target = new URLSearchParams(location.search).get("to");
    if (target && toc.some((x) => x.id === target)) {
      setTimeout(() => go(target), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, toc.length]);

  // Shared component maps for <Trans/> inline markup
  const c = {
    b: <b />,
    log: <Link to="/app/log" className={linkCls} />,
    reports: <Link to="/app/reports" className={linkCls} />,
    access: <Link to="/app/reports" className={linkCls} />,
    context: <Link to="/app/context" className={linkCls} />,
    history: <Link to="/app/history" className={linkCls} />,
    dashboard: <Link to="/app/dashboard" className={linkCls} />,
    team: <Link to="/app/team" className={linkCls} />,
    org: <Link to="/app/organization" className={linkCls} />,
    billing: <Link to="/app/billing" className={linkCls} />,
    settings: <Link to="/app/settings" className={linkCls} />,
    support: <a href="mailto:support@jotbay.ai" className={linkCls} />,
    privacy: <button onClick={() => go("privacy")} className={linkCls} />,
  };
  const T = ({ k }) => <Trans i18nKey={k} components={c} />;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="help-page">
      <div className="mb-8">
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-navy">{t("help.title")}</h1>
        <p className="text-slatebody mt-2 text-base">
          {isOrg ? t("help.subtitleOrg") : t("help.subtitleIndividual")}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* Sticky ToC */}
        <nav className="hidden lg:block" data-testid="help-toc">
          <div className="sticky top-6 space-y-1">
            <p className="font-mono text-[10px] uppercase tracking-wide text-slatebody mb-2 px-3">{t("help.onThisPage")}</p>
            {toc.map((x) => (
              <button key={x.id} onClick={() => go(x.id)} data-testid={`help-toc-${x.id}`}
                className={`block w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${active === x.id ? "bg-accent text-navy font-medium" : "text-slatebody hover:text-navy hover:bg-slate-50"}`}>
                {t(`help.toc.${x.id}`)}
              </button>
            ))}
          </div>
        </nav>

        <div className="space-y-6 min-w-0">
          <Card id="welcome" icon={Rocket} title={t("help.welcome.cardTitle")}>
            <p className="text-sm text-slatebody leading-relaxed">{t("help.welcome.p1")}</p>
            <p className="text-sm text-slatebody leading-relaxed">
              <T k={isOrg ? "help.welcome.p2Org" : "help.welcome.p2Individual"} />
            </p>
          </Card>

          <Card id="quickstart" icon={ArrowRight} title={t("help.quickstart.cardTitle")} badge={t("help.badge.steps3")}>
            <div className="space-y-5">
              <Step n="1" title={t("help.quickstart.step1Title")}><T k="help.quickstart.step1" /></Step>
              <Step n="2" title={t("help.quickstart.step2Title")}><T k="help.quickstart.step2" /></Step>
              <Step n="3" title={isOrg ? t("help.quickstart.step3TitleOrg") : t("help.quickstart.step3TitleIndividual")}>
                <T k={isOrg ? "help.quickstart.step3Org" : "help.quickstart.step3Individual"} />
              </Step>
            </div>
          </Card>

          <Card id="logging" icon={PenLine} title={t("help.logging.cardTitle")}>
            <ul className="space-y-2.5">
              <Bullet><T k="help.logging.b1" /></Bullet>
              <Bullet><T k="help.logging.b2" /></Bullet>
              <Bullet><T k="help.logging.b3" /></Bullet>
              <Bullet><T k="help.logging.b4" /></Bullet>
            </ul>
          </Card>

          <Card id="reports" icon={FileText} title={t("help.reports.cardTitle")}>
            <ul className="space-y-2.5">
              <Bullet><T k="help.reports.b1" /></Bullet>
              <Bullet><T k="help.reports.b2" /></Bullet>
              <Bullet><T k="help.reports.b3" /></Bullet>
              <Bullet><T k="help.reports.b4" /></Bullet>
            </ul>
          </Card>

          <Card id="styles" icon={LayoutTemplate} title={t("help.styles.cardTitle")}>
            <p className="text-sm text-slatebody leading-relaxed"><T k="help.styles.intro" /></p>
            <ul className="space-y-2.5 mt-3">
              <Bullet><T k="help.styles.executive" /></Bullet>
              <Bullet><T k="help.styles.standard" /></Bullet>
              <Bullet><T k="help.styles.narrative" /></Bullet>
              <Bullet><T k="help.styles.bullets" /></Bullet>
              <Bullet><T k="help.styles.technical" /></Bullet>
              <Bullet><T k="help.styles.formal" /></Bullet>
              <Bullet><T k="help.styles.custom" /></Bullet>
            </ul>
            <p className="text-sm text-slatebody leading-relaxed mt-3"><T k="help.styles.lengthNote" /></p>
          </Card>

          <Card id="customtpl" icon={Sparkles} title={t("help.customtpl.cardTitle")}>
            <p className="text-sm text-slatebody leading-relaxed"><T k="help.customtpl.intro" /></p>
            <ul className="space-y-2.5 mt-3">
              <Bullet><T k="help.customtpl.b1" /></Bullet>
              <Bullet><T k="help.customtpl.b2" /></Bullet>
              <Bullet><T k="help.customtpl.b3" /></Bullet>
              <Bullet><T k="help.customtpl.b4" /></Bullet>
              <Bullet><T k="help.customtpl.b5" /></Bullet>
            </ul>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 mt-3">
              <div className="flex items-center gap-2 mb-1 text-emerald-700"><Lock className="h-4 w-4" /><span className="font-medium text-sm">{t("help.customtpl.privacyTitle")}</span></div>
              <p className="text-sm text-slatebody leading-relaxed"><T k="help.customtpl.privacy" /></p>
            </div>
          </Card>

          <Card id="context" icon={Layers} title={t("help.context.cardTitle")}>
            <p className="text-sm text-slatebody leading-relaxed"><T k="help.context.p1" /></p>
            <ul className="space-y-2.5">
              <Bullet><T k="help.context.b1" /></Bullet>
              <Bullet><T k="help.context.b3" /></Bullet>
            </ul>
            <p className="text-sm text-slatebody leading-relaxed"><T k="help.context.p2" /></p>
          </Card>

          <Card id="history" icon={HistoryIcon} title={t("help.history.cardTitle")}>
            <ul className="space-y-2.5">
              <Bullet><T k="help.history.b1" /></Bullet>
              <Bullet><T k="help.history.b2" /></Bullet>
              <Bullet><T k="help.history.b3" /></Bullet>
            </ul>
          </Card>

          {/* PRIVACY EXPLAINER */}
          <Card id="privacy" icon={ShieldCheck} title={t("help.privacy.cardTitle")} badge={t("help.badge.important")}>
            {isOrg ? (
              <>
                <p className="text-sm text-slatebody leading-relaxed">{t("help.privacy.orgIntro")}</p>
                <div className="grid sm:grid-cols-2 gap-4 mt-2">
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                    <div className="flex items-center gap-2 mb-2 text-emerald-700"><EyeOff className="h-4 w-4" /><span className="font-medium text-sm">{t("help.privacy.alwaysPrivate")}</span></div>
                    <ul className="space-y-2">
                      <Bullet><T k="help.privacy.ap1" /></Bullet>
                      <Bullet><T k="help.privacy.ap2" /></Bullet>
                      <Bullet><T k="help.privacy.ap3" /></Bullet>
                    </ul>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-background p-4">
                    <div className="flex items-center gap-2 mb-2 text-navy"><Eye className="h-4 w-4" /><span className="font-medium text-sm">{t("help.privacy.visibleToAuth")}</span></div>
                    <ul className="space-y-2">
                      <Bullet><T k="help.privacy.va1" /></Bullet>
                      <Bullet><T k="help.privacy.va3" /></Bullet>
                    </ul>
                  </div>
                </div>
                <p className="text-sm text-slatebody leading-relaxed mt-3"><T k="help.privacy.orgControl" /></p>
              </>
            ) : (
              <>
                <p className="text-sm text-slatebody leading-relaxed">{t("help.privacy.indP1")}</p>
                <ul className="space-y-2.5">
                  <Bullet><T k="help.privacy.indB1" /></Bullet>
                  <Bullet><T k="help.privacy.indB2" /></Bullet>
                  <Bullet><T k="help.privacy.indB3" /></Bullet>
                </ul>
              </>
            )}
          </Card>

          {isSupervisor && (
            <Card id="team" icon={Users} title={t("help.team.cardTitle")} badge={t("help.badge.supervisor")}>
              <ul className="space-y-2.5">
                <Bullet><T k="help.team.b1" /></Bullet>
                <Bullet><T k="help.team.b2" /></Bullet>
                <Bullet><T k="help.team.b3" /></Bullet>
                <Bullet><T k="help.team.b4" /></Bullet>
              </ul>
            </Card>
          )}

          {isAdmin && (
            <Card id="org" icon={Building2} title={t("help.org.cardTitle")} badge={t("help.badge.admin")}>
              <p className="text-sm text-slatebody leading-relaxed"><T k="help.org.intro" /></p>
              <div className="space-y-5 mt-2">
                <Step n="1" title={t("help.org.step1Title")}>{t("help.org.step1")}</Step>
                <Step n="2" title={t("help.org.step2Title")}>{t("help.org.step2")}</Step>
                <Step n="3" title={t("help.org.step3Title")}><T k="help.org.step3" /></Step>
                <Step n="4" title={t("help.org.step4Title")}><T k="help.org.step4" /></Step>
                <Step n="5" title={t("help.org.step5Title")}><T k="help.org.step5" /></Step>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 mt-2">
                <div className="flex items-center gap-2 mb-1 text-amber-700"><Lock className="h-4 w-4" /><span className="font-medium text-sm">{t("help.org.cannotTitle")}</span></div>
                <p className="text-sm text-slatebody leading-relaxed"><T k="help.org.cannot" /></p>
              </div>
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2 text-navy"><LayoutTemplate className="h-4 w-4" /><span className="font-medium text-sm">{t("help.orgtpl.title")}</span></div>
                <ul className="space-y-2.5">
                  <Bullet><T k="help.orgtpl.b1" /></Bullet>
                  <Bullet><T k="help.orgtpl.b2" /></Bullet>
                  <Bullet><T k="help.orgtpl.b3" /></Bullet>
                </ul>
              </div>
            </Card>
          )}

          <Card id="billing" icon={CreditCard} title={t("help.billing.cardTitle")}>
            <ul className="space-y-2.5">
              <Bullet><T k="help.billing.b1" /></Bullet>
              {isOrg ? <Bullet><T k="help.billing.b2Org" /></Bullet> : <Bullet><T k="help.billing.b2Individual" /></Bullet>}
              <Bullet><T k="help.billing.b3" /></Bullet>
            </ul>
          </Card>

          <Card id="faq" icon={HelpCircle} title={t("help.faq.cardTitle")}>
            <div className="space-y-4">
              <div><p className="font-medium text-navy text-sm">{t("help.faq.q1")}</p><p className="text-sm text-slatebody mt-1">{t("help.faq.a1")}</p></div>
              {isOrg && <div><p className="font-medium text-navy text-sm">{t("help.faq.q2")}</p><p className="text-sm text-slatebody mt-1">{t("help.faq.a2")}</p></div>}
              <div><p className="font-medium text-navy text-sm">{t("help.faq.q3")}</p><p className="text-sm text-slatebody mt-1">{t("help.faq.a3")}</p></div>
              <div><p className="font-medium text-navy text-sm">{t("help.faq.q4")}</p><p className="text-sm text-slatebody mt-1">{isOrg ? t("help.faq.a4Org") : t("help.faq.a4Individual")}</p></div>
            </div>
          </Card>

          <div className="text-center py-4">
            <p className="text-sm text-slatebody"><T k="help.stillStuck" /></p>
          </div>
        </div>
      </div>
    </div>
  );
}
